// imports section Start
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  externalgetApi,
  externalpostApi,
} from "../../../services/external/externalApis";
import { COMMON_API_04, COMMON_API_05 } from "../../../api/ApiModel";
import { styled, useTheme } from "@mui/material/styles";
import { useStyles } from "../../../assets/styles/Theme";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  IconButton,
  Stack,
  TextField,
  MenuItem,
  Card,
  CardContent,
} from "@mui/material";
import { addSlip, deleteSlip } from "../../../store/ReduxSlipSlice";
// imports section End

const Item = styled(Paper)(({ theme }) => ({
  width: "100%",
  padding: "4px",
}));

function CashierSlip({ formData = {}, setFormData }) {
  // Hooks start
  const theme = useTheme();
  const styles = useStyles(theme);
  const [value, setValue] = React.useState("one");
  const [banks, setBanks] = useState([]);
  const [selectedBank, setSelectedBank] = useState("");
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [error, setError] = useState(null);
  const [addedSlips, setAddedSlips] = useState([]);
  const dispatch = useDispatch();
  const slipList = useSelector((state) => state.slips.slipList);

  useEffect(() => {
    fetchBanks();
  }, []);

  useEffect(() => {
    fetchBranches();
  }, [selectedBank]);
  // Hooks end

  // Handlers start

  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-US", {
      // style: "currency",
      // currency: "LKR",
    }).format(price);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const nameParts = name.split("."); // Split the name by '.' to handle nested fields

    setFormData((prev) => {
      const updatedFormData = { ...prev };

      // Initialize the "deposit" object if it's undefined
      if (!updatedFormData.deposit) {
        updatedFormData.deposit = {};
      }

      if (nameParts.length === 2) {
        updatedFormData[nameParts[0]] = {
          ...updatedFormData[nameParts[0]],
          [nameParts[1]]: value,
        };
      } else if (nameParts.length === 3) {
        updatedFormData[nameParts[0]] = {
          ...updatedFormData[nameParts[0]],
          [nameParts[1]]: {
            ...updatedFormData[nameParts[0]][nameParts[1]],
            [nameParts[2]]: value,
          },
        };
      } else {
        updatedFormData[name] = value;
      }

      return updatedFormData;
    });
  };

  // Handle bank selection and update form data
  const handleBankChange = (event) => {
    const { value } = event.target;
    console.log("Selected Bank:", value); // Log the selected bank
    setSelectedBank(value);
    setFormData((prev) => ({
      ...prev,
      deposit: {
        ...prev.deposit,
        slip: {
          ...prev.deposit.slip,
          slipbank: value,
        },
      },
    }));
    setBranches([]); // Reset branches when changing bank
  };

  // Handle branch selection and update form data
  const handleBranchChange = (event) => {
    const { value } = event.target;
    console.log(value); // Log the selected branch code
    setSelectedBranch(value);
    setFormData((prev) => ({
      ...prev,
      deposit: {
        ...prev.deposit,
        slip: {
          ...prev.deposit.slip,
          slipbranch: value,
        },
      },
    }));
  };

  const handleAddPayment = () => {
    // Add slip to Redux state
    dispatch(addSlip(formData.deposit.slip));

    // Clear the cheque form data after adding it
    setFormData((prev) => ({
      ...prev,
      deposit: {
        ...prev.deposit,
        slip: {}, // Reset only the 'cheque' field
      },
    }));
  };

  const handleDeletePayment = (index) => {
    // Remove card by index from Redux state
    dispatch(deleteSlip(index));
  };
  // Handlers end

  // API calls start
  // Fetch banks on component mount

  const fetchBanks = async () => {
    try {
      const response = await externalgetApi(COMMON_API_04, "GET");
      setBanks(response);
    } catch (error) {
      console.error("Error fetching banks:", error);
      setError(error);
    }
  };

  // Fetch branches based on selected bank

  const fetchBranches = async () => {
    if (selectedBank) {
      try {
        const params = { BANKCODE: selectedBank };
        const response = await externalgetApi(COMMON_API_05, "GET", params);
        setBranches(response); // Assuming response is the array of branches
      } catch (error) {
        console.error("Error fetching branches:", error);
        setError(error);
      }
    }
  };

  // API calls end

  return (
    <Box sx={styles.cs_cash}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Typography
            sx={{ ...theme.typography.h2, color: theme.palette.text.primary }}
          >
            Slip Deposit
          </Typography>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <TextField
            label="Name"
            name="deposit.slip.slipname"
            variant="outlined"
            fullWidth
            size="small"
            value={formData?.deposit?.slip?.slipname || ""}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <TextField
            label="Amount"
            name="deposit.slip.slipamount"
            variant="outlined"
            fullWidth
            size="small"
            value={formData?.deposit?.slip?.slipamount || ""}
            onChange={handleChange}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <TextField
            select
            label="Bank"
            name="deposit.slip.slipbank" // Nested in the "deposit" object
            value={formData?.deposit?.slip?.slipbank || ""}
            onChange={handleBankChange}
            size="small"
            fullWidth
          >
            {banks.map((bank) => (
              <MenuItem key={bank.BANKCODE} value={bank.BANKCODE}>
                {bank.BANKNAME}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <TextField
            select
            label="Branch"
            name="deposit.slip.slipbranch"
            value={formData?.deposit?.slip?.slipbranch || ""}
            onChange={handleBranchChange}
            size="small"
            fullWidth
            disabled={!selectedBank}
          >
            {branches.map((branch) => (
              <MenuItem key={branch.BRANCHCODE} value={branch.BRANCHCODE}>
                {branch.BRANCHNAME}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        {/* <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <TextField
            id="outlined-basic"
            label="Remark"
            variant="outlined"
            fullWidth
            size="small"
          />
        </Grid> */}
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Button
            variant="contained"
            size="medium"
            sx={styles.btn_primary}
            fullWidth
            onClick={handleAddPayment}
          >
            Add New
          </Button>
        </Grid>
      </Grid>

      <hr sx={{ ...styles.border_bottom }}></hr>

      <Box pt={2}>
        <Stack spacing={2}>
          {slipList.map((slip, index) => (
            <Card
              key={index}
              sx={{
                borderRadius: 2,
                boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                padding: "12px",
                backgroundColor: theme.palette.background.default,
                alignItems: "center",
                pt: 0,
              }}
            >
              <CardContent>
                <Grid
                  container
                  spacing={2}
                  display={"flex"}
                  alignItems={"center"}
                >
                  <Grid
                    item
                    xs={12}
                    sm={4}
                    display={"flex"}
                    flexDirection={"column"}
                  >
                    <Typography variant="caption">Name</Typography>
                    <Typography variant="sub_title" fontWeight={600}>
                      {slip.slipname}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={3}
                    display={"flex"}
                    flexDirection={"column"}
                  >
                    <Typography variant="caption">Bank</Typography>
                    <Typography variant="sub_title" fontWeight={600}>
                      {slip.slipbank}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={3}
                    textAlign={"right"}
                    display={"flex"}
                    flexDirection={"column"}
                  >
                    <Typography variant="caption">Amount</Typography>
                    <Typography variant="sub_title" fontWeight={600}>
                      {formatPrice(slip.slipamount)}
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={2}
                    display={"flex"}
                    justifyContent={"flex-end"} // Align button to the right
                    alignItems={"center"}
                  >
                    <IconButton
                      onClick={() => handleDeletePayment(index)}
                      sx={{
                        ...styles.cs_cash_delete_icon,
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

export default CashierSlip;
